import { TMatch } from "@/api/entities/matches.entity";
import { useGetMatchInfo } from "@/api/services/main/match";
import { FCC } from "@/types/common";
import { createContext, MouseEventHandler, useContext, useMemo } from "react";
import { matchSchema } from "@/features/match/schema/matchSchema";
import { useVisible } from "@/hooks/useVisible";

export type InitSetupType = {
  currentStep: number;
  checkFinishInitStep: MouseEventHandler<HTMLElement>;
  isLoading: boolean;
  match?: TMatch;
};

export const InitSetupContext = createContext<InitSetupType | undefined>(undefined);

export const InitSetupProvider: FCC = ({ children }) => {
  const matchInfoStep = useVisible();

  const { data: matchInfoResult, isLoading: isMatchInfoLoading } = useGetMatchInfo();

  const match = useMemo(() => {
    return matchInfoResult?.data;
  }, [matchInfoResult]);

  const isPassMatchInfoStep = useMemo(() => {
    if (!matchInfoResult) return false;
    const compareKeys: (keyof TMatch)[] = [];
    Object.entries(matchSchema.fields).forEach(([key, value]) => {
      const fieldDes = value.describe();
      if ("optional" in fieldDes && fieldDes.optional === false && "nullable" in fieldDes && fieldDes.nullable === false) {
        compareKeys.push(key as keyof TMatch);
      }
    });
    const isFullFillRso = !compareKeys.some(
      (key) => matchInfoResult.data[key] === null || matchInfoResult.data[key] === undefined || matchInfoResult.data[key] === "",
    );

    return isFullFillRso;
  }, [matchInfoResult]);

  const currentStep = useMemo(() => {
    if (!isPassMatchInfoStep) return 1;
    return 5;
  }, [isPassMatchInfoStep]);

  const checkFinishInitStep: MouseEventHandler<HTMLElement> = (e) => {
    if (!isPassMatchInfoStep) {
      e.preventDefault();
      matchInfoStep.open();
      return;
    }
  };

  return (
    <InitSetupContext.Provider
      value={{
        match,
        currentStep,
        checkFinishInitStep,
        isLoading: isMatchInfoLoading,
      }}
    >
      {children}
    </InitSetupContext.Provider>
  );
};

export const useInitSetup = (): InitSetupType => {
  const context = useContext(InitSetupContext);
  if (!context) {
    throw new Error("useInitSetup must be used within a InitSetupProvider");
  }
  return context;
};
